import { ulid } from 'ulid';
import type {
	CreateVehicleInput,
	UpdateVehicleInput,
	VehicleViewModel
} from '@/graphql/generated';

export interface VehicleFormSubmitData {
	vehicleId?: string;
	plateNumber: string;
	vehicleTypeId: string;
	customerId?: string | null;
	customerDisplayName?: string; // Display name for existing customer association
	make?: string | null;
	model?: string | null;
	color?: string | null;
	isActive: boolean;
}

const normaliseString = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
};

const normaliseVehicleTypeId = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const buildCreateVehicleInput = (
	formData: VehicleFormSubmitData,
	idempotentVehicleId: string
): CreateVehicleInput => {
	const input: CreateVehicleInput = {
		vehicleId: idempotentVehicleId,
		isActive: formData.isActive
	};

	// Handle plateNumber - required by form validation but nullable in schema
	const plateNumber = normaliseString(formData.plateNumber);
	if (plateNumber !== null) {
		input.plateNumber = plateNumber;
	}

	// Handle vehicleTypeId - required by form validation but nullable in schema
	const vehicleTypeId = normaliseVehicleTypeId(formData.vehicleTypeId);
	if (vehicleTypeId !== null) {
		input.vehicleTypeId = vehicleTypeId;
	}

	// Handle optional customerId
	const customerId = normaliseString(formData.customerId);
	if (customerId !== null) {
		input.customerId = customerId;
	}

	// Handle optional fields
	const make = normaliseString(formData.make);
	if (make !== null) {
		input.make = make;
	}

	const model = normaliseString(formData.model);
	if (model !== null) {
		input.model = model;
	}

	const color = normaliseString(formData.color);
	if (color !== null) {
		input.color = color;
	}

	return input;
};

export const buildUpdateVehicleInput = (
	formData: VehicleFormSubmitData,
	vehicleId: string
): UpdateVehicleInput => {
	const input: UpdateVehicleInput = {
		vehicleId,
		isActive: formData.isActive
	};

	// Handle plateNumber - required by form validation but nullable in schema
	const plateNumber = normaliseString(formData.plateNumber);
	if (plateNumber !== null) {
		input.plateNumber = plateNumber;
	}

	// Handle vehicleTypeId - required by form validation but nullable in schema
	const vehicleTypeId = normaliseVehicleTypeId(formData.vehicleTypeId);
	if (vehicleTypeId !== null) {
		input.vehicleTypeId = vehicleTypeId;
	}

	// Handle optional customerId - can be null to clear association
	const customerId = normaliseString(formData.customerId);
	input.customerId = customerId;

	// Handle optional fields
	const make = normaliseString(formData.make);
	if (make !== null) {
		input.make = make;
	}

	const model = normaliseString(formData.model);
	if (model !== null) {
		input.model = model;
	}

	const color = normaliseString(formData.color);
	if (color !== null) {
		input.color = color;
	}

	return input;
};

export const mapVehicleToFormData = (
	vehicle: VehicleViewModel
): VehicleFormSubmitData => {
	// Determine vehicleTypeId - fix the nested ternary
	let vehicleTypeId = '';
	if (vehicle.vehicleTypeId) {
		vehicleTypeId = String(vehicle.vehicleTypeId);
	} else if (vehicle.vehicleType) {
		vehicleTypeId = String(vehicle.vehicleType.vehicleTypeId);
	}

	// Generate customer display name if customer exists
	let customerDisplayName = '';
	if (vehicle.customer) {
		const customer = vehicle.customer;
		// Prefer displayName if available (from backend calculation)
		if (customer.displayName) {
			customerDisplayName = customer.displayName;
		} else if (customer.isCompany && customer.firmName) {
			customerDisplayName = customer.firmName;
		} else if (customer.firstName || customer.lastName) {
			customerDisplayName = [customer.firstName, customer.lastName]
				.filter(Boolean)
				.join(' ');
		} else if (customer.firmName) {
			// Fallback to firmName even if not marked as company
			customerDisplayName = customer.firmName;
		} else if (customer.email) {
			// Use email as fallback instead of ID
			customerDisplayName = customer.email;
		} else {
			// Last resort - use customer ID
			customerDisplayName = customer.customerId;
		}
	}

	return {
		vehicleId: vehicle.vehicleId,
		plateNumber: vehicle.plateNumber ?? '',
		vehicleTypeId,
		customerId: vehicle.customerId ?? null,
		customerDisplayName,
		make: vehicle.make ?? null,
		model: vehicle.model ?? null,
		color: vehicle.color ?? null,
		isActive: vehicle.isActive
	};
};

export const generateIdempotentVehicleId = (): string => {
	return ulid();
};
